import { Compilation, CompilationPreview } from "@turtlewave/types";

import { ContentFetchError } from "../content/errors";
import { apiConfig } from "../utils/client";

import {
  validateCompilation,
  validateCompilationPreviewArray,
} from "./schemas";

class CompilationService {
  private static instance: CompilationService;

  private constructor() {}

  static getInstance(): CompilationService {
    if (!CompilationService.instance) {
      CompilationService.instance = new CompilationService();
    }
    return CompilationService.instance;
  }

  /**
   * Fetch all compilation previews
   * @returns Promise resolving to array of CompilationPreview
   * @throws ContentFetchError - On fetch failure
   * @throws ContentValidationError - On validation failure
   */
  async getAllCompilationPreviews(init?: {
    signal?: AbortSignal;
  }): Promise<CompilationPreview[]> {
    try {
      const response = await fetch(
        apiConfig.compilations.list({ preview: "true" }),
        { signal: init?.signal },
      );

      if (!response.ok) {
        throw new ContentFetchError(
          `Failed to fetch compilations: ${response.status} ${response.statusText}`,
          undefined,
          response.status,
        );
      }

      const rawData = await response.json();
      return validateCompilationPreviewArray(rawData.compilations);
    } catch (error) {
      if (error instanceof ContentFetchError) throw error;
      if (error instanceof Error && error.name === "ContentValidationError") {
        throw error;
      }
      throw new ContentFetchError("Failed to fetch compilations", error);
    }
  }

  /**
   * Fetch a compilation by ID
   * @param id - Compilation ID
   * @returns Promise resolving to Compilation
   * @throws ContentFetchError - On fetch failure
   * @throws ContentValidationError - On validation failure
   */
  async getCompilationById(
    id: string,
    init?: { signal?: AbortSignal },
  ): Promise<Compilation> {
    try {
      const response = await fetch(apiConfig.compilations.byId(id), {
        signal: init?.signal,
      });
      if (!response.ok) {
        throw new ContentFetchError(
          `Failed to fetch compilation: ${response.status} ${response.statusText}`,
          undefined,
          response.status,
        );
      }

      const rawData = await response.json();
      return validateCompilation(rawData);
    } catch (error) {
      if (error instanceof ContentFetchError) throw error;
      if (error instanceof Error && error.name === "ContentValidationError") {
        throw error;
      }
      throw new ContentFetchError(`Failed to fetch compilation: ${id}`, error);
    }
  }
}

export const compilationService = CompilationService.getInstance();
